import React, { Component as BaseComponent } from 'react';
import RubikCubeWorkspace from './CubeContainer';

const emptySolverSnapshot = {
    mode: 'idle',
    message: 'Ready.',
    solutionNotation: [],
    currentMove: '',
    totalMoves: 0,
    movesRemaining: 0,
    nodesVisited: 0,
    searchMilliseconds: 0,
    phaseOneDepth: 0,
    phaseTwoDepth: 0,
    metrics: null,
    validation: null
};

class RubikCubeApplication extends BaseComponent {
    constructor(componentProperties) {
        super(componentProperties);
        this.state = { solverSnapshot: emptySolverSnapshot };
        this.statusTimerIdentifier = null;
        this.reloadCubePage = this.reloadCubePage.bind(this);
        this.scrambleCubePage = this.scrambleCubePage.bind(this);
        this.solveCube = this.solveCube.bind(this);
        this.pauseSolver = this.pauseSolver.bind(this);
        this.continueSolver = this.continueSolver.bind(this);
        this.stepSolver = this.stepSolver.bind(this);
        this.undoMove = this.undoMove.bind(this);
        this.refreshSolverSnapshot = this.refreshSolverSnapshot.bind(this);
    }

    componentDidMount() {
        this.statusTimerIdentifier = setInterval(this.refreshSolverSnapshot, 100);
    }

    componentWillUnmount() {
        if (this.statusTimerIdentifier) clearInterval(this.statusTimerIdentifier);
    }

    refreshSolverSnapshot() {
        if (!this.cubeWorkspace) return;
        const nextSnapshot = this.cubeWorkspace.getSolverStatus();
        this.setState({ solverSnapshot: nextSnapshot });
    }

    reloadCubePage() {
        window.location.reload();
    }

    scrambleCubePage() {
        if (this.cubeWorkspace) this.cubeWorkspace.scrambleCube();
    }

    solveCube() {
        if (this.cubeWorkspace) this.cubeWorkspace.solveCube();
    }

    pauseSolver() {
        if (this.cubeWorkspace) this.cubeWorkspace.pauseSolver();
    }

    continueSolver() {
        if (this.cubeWorkspace) this.cubeWorkspace.continueSolver();
    }

    stepSolver() {
        if (this.cubeWorkspace) this.cubeWorkspace.stepSolver();
    }

    undoMove() {
        if (this.cubeWorkspace) this.cubeWorkspace.undoLastMove();
    }

    renderDecisionPanel(snapshot) {
        const metrics = snapshot.metrics || {};
        const validation = snapshot.validation || {};
        const solutionText = snapshot.solutionNotation && snapshot.solutionNotation.length ?
            snapshot.solutionNotation.join(' ') : '—';

        return (
            <aside className="decision-engine-panel">
                <div className="decision-engine-title">ZERO-TRAINING AI™ DECISION ENGINE™</div>
                <div className={`solver-state solver-state-${snapshot.mode || 'idle'}`}>
                    {snapshot.message || 'Ready.'}
                </div>
                <div className="decision-metric-grid">
                    <span>State valid</span><strong>{validation.isValid === false ? 'NO' : 'YES'}</strong>
                    <span>Current move</span><strong>{snapshot.currentMove || '—'}</strong>
                    <span>Moves remaining</span><strong>{snapshot.movesRemaining || 0}</strong>
                    <span>Misplaced corners</span><strong>{metrics.misplacedCorners || 0}</strong>
                    <span>Twisted corners</span><strong>{metrics.twistedCorners || 0}</strong>
                    <span>Misplaced edges</span><strong>{metrics.misplacedEdges || 0}</strong>
                    <span>Flipped edges</span><strong>{metrics.flippedEdges || 0}</strong>
                    <span>Correct pieces</span><strong>{metrics.correctlyPlacedPieces || 0}/20</strong>
                    <span>Legal immediate moves</span><strong>{metrics.legalImmediateMoves || 18}</strong>
                    <span>Non-reversing futures</span><strong>{metrics.nonReversingFutureMoves || 15}</strong>
                    <span>Search nodes</span><strong>{snapshot.nodesVisited || 0}</strong>
                    <span>Search time</span><strong>{snapshot.searchMilliseconds || 0} ms</strong>
                </div>
                <div className="solution-label">Calculated sequence</div>
                <div className="solution-sequence">{solutionText}</div>
                <div className="deterministic-note">
                    Equations + legal-state constraints + exact search. No LLM. No training data.
                </div>
            </aside>
        );
    }

    render() {
        const isEmbeddedCube = window.location.search.indexOf('embedded=1') !== -1;
        const shellClassName = isEmbeddedCube ?
            'application-shell embedded-cube-shell' :
            'application-shell solver-application-shell';
        const snapshot = this.state.solverSnapshot || emptySolverSnapshot;

        return (
            <div className={shellClassName}>
                {!isEmbeddedCube &&
                    <div>
                        <h3 className="solver-page-title">
                            <span style={{ fontSize: '0.7em' }}>
                                Zero-Training AI™ Rubik Cube Solver
                            </span>
                            &nbsp;&nbsp;
                            <a href="/Rubik/Index" target="_blank" rel="noopener noreferrer">
                                <span style={{ fontSize: '0.7em' }}>Read Article</span>
                            </a>
                        </h3>
                        <div className="external-cube-controls solver-control-bar">
                            <button type="button" className="button red" onClick={this.reloadCubePage}>Reset</button>
                            <button type="button" className="button green" onClick={this.scrambleCubePage}>Scramble</button>
                            <button type="button" className="button blue" onClick={this.solveCube}>AI Solve</button>
                            <button type="button" className="button" onClick={this.pauseSolver}>Pause</button>
                            <button type="button" className="button" onClick={this.continueSolver}>Continue</button>
                            <button type="button" className="button" onClick={this.stepSolver}>Next Move</button>
                            <button type="button" className="button" onClick={this.undoMove}>Undo</button>
                        </div>
                    </div>
                }

                <div className={isEmbeddedCube ? 'app' : 'app app-with-decision-panel'}>
                    <RubikCubeWorkspace
                        ref={workspaceComponent => this.cubeWorkspace = workspaceComponent}
                    />
                </div>

                {!isEmbeddedCube && this.renderDecisionPanel(snapshot)}
            </div>
        );
    }
}

export default RubikCubeApplication;
